import type {
  AdminUsersResponse,
  CreditTopUpResponse,
  GrantMembershipResponse,
  MembershipPlanType,
} from '../types/api';
import { getAuthHeader, clearCredentials } from './auth';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8082';

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const authHeader = getAuthHeader();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader ? { Authorization: authHeader } : {}),
      ...options?.headers,
    },
  });

  if (response.status === 401) {
    clearCredentials();
    window.location.reload();
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const text = await response.text();
    let message = `Request failed: ${response.status}`;
    try {
      const json = JSON.parse(text) as { message?: string };
      if (json.message) message = json.message;
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

export async function verifyCredentials(): Promise<boolean> {
  try {
    await fetchApi<AdminUsersResponse>('/admin/api/users?page=1&limit=1');
    return true;
  } catch {
    return false;
  }
}

export async function fetchUsers(page: number, limit: number): Promise<AdminUsersResponse> {
  return fetchApi<AdminUsersResponse>(`/admin/api/users?page=${page}&limit=${limit}`);
}

export async function deleteUser(userId: string): Promise<void> {
  await fetchApi<void>(`/admin/api/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

export async function topUpUserCredits(
  userId: string,
  credits: number,
  note?: string
): Promise<CreditTopUpResponse> {
  return fetchApi<CreditTopUpResponse>(
    `/admin/api/users/${encodeURIComponent(userId)}/credits/top-up`,
    {
      method: 'POST',
      body: JSON.stringify({ credits, note }),
    }
  );
}

export async function grantMembership(
  userId: string,
  planType: MembershipPlanType,
  months?: number
): Promise<GrantMembershipResponse> {
  return fetchApi<GrantMembershipResponse>(
    `/admin/api/users/${encodeURIComponent(userId)}/subscription`,
    {
      method: 'POST',
      body: JSON.stringify({
        planType,
        ...(planType === 'monthly' ? { months } : {}),
      }),
    }
  );
}

export async function revokeMembership(userId: string): Promise<void> {
  await fetchApi<void>(
    `/admin/api/users/${encodeURIComponent(userId)}/subscription`,
    { method: 'DELETE' }
  );
}
